import { Button } from "@/components/atom/Button";
import Dialog from "@/components/atom/Dialog";
import DialogFooterContainer from "@/components/atom/Dialog/DialogFooterContainer";
import Typo from "@/components/atom/Typo";
import { ReactComponent as SearchIcon } from "@/src/assets/icons/icon-search.svg";
import { ReactComponent as ErrorIcon } from "@/src/assets/icons/icon-error-circle.svg";
import { useAppSelector } from "@/src/store";
import { GenerateBuyerEmployeeDto } from "@/src/store/apis/client/buyer/interface";
import colorSet from "@/src/styles/color";
import { useRef, useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { Empty } from "antd";

interface ClientIntegrationInviteCodeDialogProps {
  onClose: () => void;
  onSave: (user: GenerateBuyerEmployeeDto) => void;
  personList: (GenerateBuyerEmployeeDto & { uniqueId: string })[];
  isLoading?: boolean;
}

const ClientIntegrationInviteCodeDialog = ({
  onSave,
  onClose,
  personList,
  isLoading,
}: ClientIntegrationInviteCodeDialogProps) => {
  const { t } = useTranslation();
  const user = useAppSelector((state) => state.auth.user);
  const dropdownRef = useRef<HTMLDivElement>(null);
  
  // 사용자 역할에 따른 초대 코드 가져오기
  const companyLinkCode = user?.exporter?.companyLinkCode || user?.importer?.companyLinkCode || "-";

  const [selectedEmail, setSelectedEmail] = useState<string>("");
  const [showEmailList, setShowEmailList] = useState<boolean>(false);
  const [emailError, setEmailError] = useState<string>("");
  const [inputValue, setInputValue] = useState<string>("");
  
  // 필터링된 이메일 목록
  const filteredPersonList = personList.filter(person => 
    person.email.toLowerCase().includes(inputValue.toLowerCase())
  );

  // 이메일 목록에서 선택
  const handleEmailSelect = (person: GenerateBuyerEmployeeDto & { uniqueId: string }) => {
    setSelectedEmail(person.email);
    setInputValue(person.email);
    setShowEmailList(false);
    setEmailError("");
  };

  // 선택 해제
  const handleClearSelection = () => {
    setSelectedEmail("");
    setInputValue("");
    setShowEmailList(false);
  };

  const handleSaveClick = () => {
    if (!selectedEmail) {
      setEmailError(t("error:required"));
      return;
    }

    // 선택된 담당자 찾기
    const selectedPerson = personList.find(
      (person) => person.email === selectedEmail
    );

    if (selectedPerson) {
      onSave(selectedPerson);
    }
  };

  // 외부 클릭 감지
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setShowEmailList(false);
        // 선택된 이메일이 없으면 입력값 초기화
        if (!selectedEmail) {
          setInputValue("");
        }
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [selectedEmail]);

  return (
    <Dialog
      title={t("common:sendClientInviteCode")}
      open
      onOpenChange={onClose}
      width={640}
      height={450}
      maxHeight={450}
      footer={
        <DialogFooterContainer>
          <Button 
            buttonGrade="tertiary" 
            buttonColor="black" 
            onClick={onClose}
            style={{ width: '292px' }}
          >
            {t("common:cancel")}
          </Button>
          <Button
            onClick={handleSaveClick}
            disabled={isLoading || !selectedEmail || !!emailError}
            isLoading={isLoading}
            style={{ width: '292px' }}
          >
            {t("common:send")}
          </Button>
        </DialogFooterContainer>
      }
    >
      <DialogContent>
        <GuideText>
          {t("common:sendClientInviteCodeGuide")}
        </GuideText>

        <CodeSection>
          <Label>{t("common:inviteCode")}</Label>
          <CodeBox>
            <Typo typoType="h3" color="blue2">
              {companyLinkCode}
            </Typo>
          </CodeBox>
        </CodeSection>

        <InputSection>
          <LabelWrapper>
            <Label>{t("common:email")}</Label>
            <RequiredMark>*</RequiredMark>
          </LabelWrapper>
          
          <EmailInputWrapper ref={dropdownRef}>
            <InputContainer 
              data-error={!!emailError && !selectedEmail}
              data-open={showEmailList}
            >
              {selectedEmail ? (
                <SelectedEmail>
                  <EmailText>{selectedEmail}</EmailText>
                  <ClearButton 
                    onClick={(e) => {
                      e.stopPropagation();
                      handleClearSelection();
                    }}
                  >
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M11.9582 3.70684C12.1372 3.88583 12.1522 4.16672 12.003 4.36274L11.9583 4.41394L8.54112 7.83227L11.9603 11.2514C12.1555 11.4466 12.1555 11.7633 11.9603 11.9585C11.7813 12.1375 11.5004 12.1524 11.3044 12.0033L11.2532 11.9585L7.83405 8.53936L4.41774 11.9576C4.22252 12.1529 3.9059 12.153 3.71064 11.9578C3.53165 11.7787 3.51664 11.4978 3.66576 11.3018L3.7105 11.2506L7.12694 7.83227L3.70973 4.41506C3.51446 4.21979 3.51446 3.90322 3.70973 3.70795C3.88872 3.52896 4.16965 3.51404 4.36563 3.6632L4.41683 3.70795L7.83405 7.12516L11.2511 3.70698C11.4463 3.51168 11.763 3.51158 11.9582 3.70684Z" fill="#166DD7"/>
                    </svg>
                  </ClearButton>
                </SelectedEmail>
              ) : (
                <StyledInput
                  value={inputValue}
                  onChange={(e) => {
                    setInputValue(e.target.value);
                    setSelectedEmail("");
                    setShowEmailList(true);
                  }}
                  onFocus={() => setShowEmailList(true)}
                  placeholder={t("sendLinkCodeDialog:common.placeholder.emailList")}
                />
              )}
              <SearchIconWrapper>
                <SearchIcon />
              </SearchIconWrapper>
            </InputContainer>
            
            {!selectedEmail && emailError && (
              <ErrorWrapper>
                <ErrorIcon width={16} height={16} />
                <ErrorText>{emailError}</ErrorText>
              </ErrorWrapper>
            )}
            
            {showEmailList && (
              <EmailListDropdown>
                {filteredPersonList.length > 0 ? (
                  filteredPersonList.map((person) => (
                    <EmailItem
                      key={person.uniqueId}
                      onClick={() => handleEmailSelect(person)}
                    >
                      <Typo typoType="b7r" color="gray2">
                        {person.email}
                      </Typo>
                    </EmailItem>
                  ))
                ) : (
                  <NoDataItem>
                    <Empty 
                      image={Empty.PRESENTED_IMAGE_SIMPLE}
                      description={t("common:noData")}
                    />
                  </NoDataItem>
                )}
              </EmailListDropdown>
            )}
          </EmailInputWrapper>
        </InputSection>
      </DialogContent>
    </Dialog>
  );
};

export default ClientIntegrationInviteCodeDialog;

const DialogContent = styled.div`
  display: flex;
  flex-direction: column;
`;

const GuideText = styled(Typo).attrs({
  typoType: "b7r",
  color: "gray2"
})`
  margin-bottom: 19px;
  line-height: 24px;
`;

const CodeSection = styled.div`
  margin-bottom: 18px;
`;

const Label = styled(Typo).attrs({
  typoType: "b7m",
  color: "gray2"
})`
  margin-bottom: 8px;
  display: block;
`;

const CodeBox = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: ${colorSet.white};
  border: 1px solid ${colorSet.gray9};
  border-radius: 8px;
  padding: 13px 0;
`;

const InputSection = styled.div`
  position: relative;
`;

const LabelWrapper = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 8px;
`;

const RequiredMark = styled.span`
  color: ${colorSet.red2};
  margin-left: 4px;
  position: relative;
  top: -2px;
`;

const EmailInputWrapper = styled.div`
  position: relative;
`;

const InputContainer = styled.div`
  position: relative;
  height: 40px;
  background: ${colorSet.white};
  border: 1px solid ${colorSet.gray9};
  border-radius: 10px;
  box-shadow: 0px 2px 4px rgba(0, 0, 0, 0.05);
  display: flex;
  align-items: center;
  padding: 0 40px 0 16px;
  outline: none;
  
  &[data-error="true"] {
    border-color: ${colorSet.red2};
  }
  
  &[data-open="true"] {
    box-shadow: none;
    border-color: ${colorSet.blue4};
  }
  
  &:hover {
    border-color: ${colorSet.gray7};
  }
  
  &:focus {
    border-color: ${colorSet.blue4};
    box-shadow: none;
  }
`;

const StyledInput = styled.input`
  flex: 1;
  border: none;
  outline: none;
  background: transparent;
  font-family: 'Pretendard', sans-serif;
  font-weight: 400;
  font-size: 15px;
  line-height: 20px;
  color: ${colorSet.gray2};
  
  &::placeholder {
    color: ${colorSet.gray7};
  }
`;

const SelectedEmail = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 10px;
  background: #F0F8FF;
  padding: 2px 8px;
  border-radius: 64px;
  height: 24px;
  box-shadow: 0px 1px 2px 0px rgba(5, 29, 57, 0.1), 0px 0px 0px 0.5px rgba(23, 98, 195, 0.2);
`;

const EmailText = styled.span`
  font-family: 'Pretendard', sans-serif;
  font-weight: 500;
  font-size: 15px;
  line-height: 20px;
  color: ${colorSet.blue2};
`;

const ClearButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;
  margin-left: 4px;
  
  svg {
    width: 16px;
    height: 16px;
  }
`;

const SearchIconWrapper = styled.div`
  pointer-events: none;
  display: flex;
  align-items: center;
  position: absolute;
  right: 16px;
  
  svg {
    width: 18px;
    height: 20px;
  }
`;

const ErrorWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  margin-top: 8px;
`;

const ErrorText = styled(Typo).attrs({
  typoType: "b9r",
  color: "red2"
})``;

const EmailListDropdown = styled.div`
  position: absolute;
  top: calc(100% + 3px);
  left: 0;
  right: 0;
  background: ${colorSet.white};
  border-radius: 8px;
  box-shadow: 0px 0px 0px 1px rgba(160, 164, 171, 0.2), 0px 2px 8px 0px rgba(5, 29, 57, 0.25);
  overflow: hidden;
  z-index: 1000;
  padding: 6px;
`;

const EmailItem = styled.div`
  padding: 8px 16px;
  cursor: pointer;
  transition: background-color 0.15s;
  border-radius: 4px;
  
  &:hover {
    background-color: ${colorSet.gray11};
  }
`;

const NoDataItem = styled.div`
  padding: 16px;
  text-align: center;
  
  .ant-empty {
    margin: 0;
  }
  
  .ant-empty-image {
    height: 40px;
    margin-bottom: 8px;
  }
  
  .ant-empty-description {
    font-size: 14px;
    color: ${colorSet.gray7};
  }
`;
